/**
 * Two languages, one set of screens (المرحلة 1.1, P26).
 *
 *   t('auth.signIn')            a key, for every screen written from 1.1 on
 *   tr('إدارة الأطباء')          an Arabic phrase, for the screens written before
 *                               it: English when en.json has it, the Arabic
 *                               itself otherwise — so a screen not yet
 *                               translated reads correctly, just in Arabic
 *
 * Which languages exist is the `uiLanguages` policy — the institution's for
 * its people, the platform's before sign-in (GET /api/auth/policy) — and
 * which one shows is the person's own choice (User.preferredLang), else the
 * one this browser last used, else the policy's default. The page turns with
 * it: <html dir="ltr" lang="en">, and every layout class is logical (ms-/pe-/
 * start-…), so English reads left to right with nothing else to change.
 *
 * The country rides along (domain/locale.js): the same answer that carries the
 * languages carries the currency and the clock, and both become active here.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ar from './ar.json';
import en from './en.json';
import { authApi } from '../api/endpoints';
import { tokenStore } from '../api/client';
import { DEFAULT_COUNTRY, setActiveLocale } from '../domain/locale';

const DICTIONARIES = { ar, en };

/** The account remembers the language, so the next device opens in it. */
function saveToAccount(lang) {
  authApi.updateProfile({ preferredLang: lang }).catch((err) => console.warn('[i18n] Could not save the language to the account:', err.message));
}
export const LANG_STORAGE_KEY = 'capsule_lang';
/** Fired by AuthContext after a sign-in, with the answer's languages, country and preferredLang. */
export const LOCALE_EVENT = 'capsule:locale';

const I18nContext = createContext(null);

function readStored() {
  try {
    const value = localStorage.getItem(LANG_STORAGE_KEY);
    return value === 'en' || value === 'ar' ? value : null;
  } catch {
    return null;
  }
}

function store(lang) {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* storage unavailable: the choice holds for this visit */
  }
}

/** 'ar' | 'ar_en' | 'en' → the languages offered. */
export function offered(languages) {
  const value = languages && languages.languages;
  if (value === 'en') return ['en'];
  if (value === 'ar_en') return ['ar', 'en'];
  return ['ar'];
}

/** Which language shows, from the policy, the person and this browser. */
export function chooseLang({ languages, preferred, stored }) {
  const allowed = offered(languages);
  if (allowed.length === 1) return allowed[0];
  for (const candidate of [preferred, stored, languages && languages.defaultLang, 'ar']) {
    if (candidate && allowed.includes(candidate)) return candidate;
  }
  return allowed[0];
}

const lookup = (dict, key) => key.split('.').reduce((node, part) => (node && typeof node === 'object' ? node[part] : undefined), dict);

const fill = (text, vars) =>
  vars ? String(text).replace(/\{(\w+)\}/g, (match, name) => (vars[name] === undefined || vars[name] === null ? match : String(vars[name]))) : String(text);

/** The translation function for one language — pure, so tests can call it. */
export function translator(lang) {
  const dict = DICTIONARIES[lang] || ar;
  const t = (key, vars) => {
    const hit = lookup(dict, key);
    const value = hit !== undefined ? hit : lookup(ar, key);
    return value === undefined ? key : fill(value, vars);
  };
  const tr = (phrase, vars) => {
    if (lang === 'ar' || phrase === null || phrase === undefined) return fill(phrase ?? '', vars);
    const english = dict.phrases && dict.phrases[phrase];
    return fill(english !== undefined ? english : phrase, vars);
  };
  return { t, tr };
}

export function I18nProvider({ children }) {
  const [languages, setLanguages] = useState({ languages: 'ar', defaultLang: 'ar' });
  const [preferred, setPreferred] = useState(null);
  const [stored, setStored] = useState(() => readStored());
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [policy, setPolicy] = useState(null);
  // The language the person picked on this visit — on the sign-in screen, or in
  // the header since. It outranks what the account had saved until they sign
  // out: someone who turned the sign-in screen to English and signed in means
  // English, and the account is told so. Without it the saved choice (or a
  // capabilities answer read before the save landed) turned the page back.
  const picked = useRef(null);

  const lang = chooseLang({ languages, preferred, stored });
  const dir = lang === 'en' ? 'ltr' : 'rtl';

  // domain/locale.js formats with this render's language and country, so it is
  // told before the children render. Told in the effect below, after them, every
  // amount and date was written in the previous one: on Saudi Arabia's profile
  // the English screen read «5,450 ج.م», and the Arabic one «SAR 5,450».
  useMemo(() => setActiveLocale({ lang, country }), [lang, country]);

  // The document turns with the language.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.documentElement.dataset.lang = lang;
  }, [lang, dir]);

  // Before anyone signs in: the platform's own rules.
  const loadPolicy = useCallback(async () => {
    try {
      const answer = await authApi.policy();
      setPolicy(answer);
      if (!tokenStore.get()) {
        if (answer.languages) setLanguages(answer.languages);
        if (answer.country) setCountry(answer.country);
      }
      return answer;
    } catch (err) {
      console.warn('[i18n] Could not read the sign-in policy:', err.message);
      return null;
    }
  }, []);
  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  /** What a sign-in or the session's capabilities said: the scope's languages and country, the person's choice. */
  const applyServerLocale = useCallback((locale) => {
    if (!locale) return;
    if (locale.languages) setLanguages(locale.languages);
    if (locale.country && locale.country.code) setCountry(locale.country);
    if (locale.preferredLang === undefined) return;
    const choice = picked.current;
    if (!choice) {
      setPreferred(locale.preferredLang || null);
      return;
    }
    setPreferred(choice);
    if (choice !== locale.preferredLang && tokenStore.get()) saveToAccount(choice);
  }, []);

  useEffect(() => {
    const onLocale = (event) => applyServerLocale(event.detail);
    window.addEventListener(LOCALE_EVENT, onLocale);
    return () => window.removeEventListener(LOCALE_EVENT, onLocale);
  }, [applyServerLocale]);

  /** The person picks a language: kept on this browser, and on the account when signed in. */
  const setLang = useCallback(
    (next) => {
      if (next !== 'ar' && next !== 'en') return;
      picked.current = next;
      store(next);
      setStored(next);
      setPreferred(next);
      if (tokenStore.get()) saveToAccount(next);
    },
    [],
  );

  /** Signed out: back to the platform's rules, the browser's own choice kept. */
  const resetToPlatform = useCallback(() => {
    setPreferred(null);
    if (policy) {
      if (policy.languages) setLanguages(policy.languages);
      if (policy.country) setCountry(policy.country);
    }
  }, [policy]);

  /** A session ended: the next person to sign in on this browser starts from their own choice. */
  const forgetPick = useCallback(() => {
    picked.current = null;
  }, []);

  const value = useMemo(() => {
    const { t, tr } = translator(lang);
    return {
      lang,
      dir,
      isRtl: dir === 'rtl',
      t,
      tr,
      setLang,
      languages: offered(languages),
      canSwitch: offered(languages).length > 1,
      country,
      policy,
      reloadPolicy: loadPolicy,
      applyServerLocale,
      resetToPlatform,
      forgetPick,
    };
  }, [lang, dir, setLang, languages, country, policy, loadPolicy, applyServerLocale, resetToPlatform, forgetPick]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** The current language's t(), tr() and the switch. Outside the provider: Arabic, no switch. */
export function useI18n() {
  const context = useContext(I18nContext);
  if (context) return context;
  const { t, tr } = translator('ar');
  return { lang: 'ar', dir: 'rtl', isRtl: true, t, tr, setLang: () => {}, languages: ['ar'], canSwitch: false, country: DEFAULT_COUNTRY, policy: null, reloadPolicy: async () => null, applyServerLocale: () => {}, resetToPlatform: () => {}, forgetPick: () => {} };
}

export default I18nProvider;
